import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { trackPageview, trackCta } from "@/lib/analytics";
import { getConsent, subscribe as subscribeConsent } from "@/lib/consent";

let gaInjected = false;
let verificationInjected = false;

// Search Console verification is a static meta tag (no cookies/tracking): always allowed.
function injectVerification(cfg) {
  if (verificationInjected) return;
  const v = cfg?.search_console_verification;
  if (!v) return;
  verificationInjected = true;
  const m = document.createElement("meta");
  m.name = "google-site-verification";
  m.content = v;
  document.head.appendChild(m);
}

// GA4 / GTM set tracking cookies → only injected after "analytics" consent.
function injectGoogle(cfg) {
  if (gaInjected) return;
  const { ga4_id, gtm_id } = cfg || {};
  if (!ga4_id && !gtm_id) return;
  gaInjected = true;
  if (ga4_id) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${ga4_id}`;
    document.head.appendChild(s);
    const i = document.createElement("script");
    i.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${ga4_id}');`;
    document.head.appendChild(i);
  }
  if (gtm_id) {
    const g = document.createElement("script");
    g.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtm_id}');`;
    document.head.appendChild(g);
  }
}

export function AnalyticsTracker() {
  const location = useLocation();
  const lastPath = useRef("");
  const cfgRef = useRef(null);

  // Load integration config, then inject scripts respecting the current consent.
  useEffect(() => {
    const apply = () => {
      const cfg = cfgRef.current;
      if (!cfg) return;
      injectVerification(cfg);
      if (getConsent().analytics) injectGoogle(cfg);
    };
    api
      .get("/settings/integrations")
      .then((r) => { cfgRef.current = r.data?.data || {}; apply(); })
      .catch(() => {});
    // Re-evaluate whenever the visitor changes consent (e.g. accepts analytics later).
    const unsub = subscribeConsent(apply);
    return unsub;
  }, []);

  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/admin")) return;
    if (path === lastPath.current) return;
    // Update the ref inside the timer (not synchronously) so React 18 StrictMode's
    // mount→cleanup→remount cycle still reschedules the initial pageview.
    const t = setTimeout(() => {
      lastPath.current = path;
      trackPageview(path, document.title);
    }, 350);
    return () => clearTimeout(t);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      const el = e.target.closest("[data-track-cta]");
      if (el) trackCta(el.getAttribute("data-track-cta") || el.textContent?.trim()?.slice(0, 80));
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return null;
}
